import { defineComponent, h } from 'vue';
import { appSettings } from '@/Shared/appSettings.js';
import careService from '@/Services/careService';
import token from '@/Services/token';
import './css/TranslationContainer.css';
import utils from '../../Shared/utils.jsx';
import api from '@/Services/api';
import methods from '../../Shared/methods';
import { QBtn, QIcon, QInput, QDialog, QCard, QCardSection, QCardActions, QSpace } from 'quasar';

export default defineComponent({
    name: 'TranslationContainer',
    components: {
        QBtn,
        QIcon,
        QInput,
        QDialog,
        QCard,
        QCardSection,
        QCardActions,
        QSpace,
        TranslationContainer: () => import('./TranslationContainer.jsx') // Recursive import for nested usage
    },
    data() {
        return {
            valueEval: null,
            editing: false,
            expanded: this.autoOpenModal,
            editedString: this.value,
            originalValue: this.value,
            keyModel: null,
            textfailed: 1,
        };
    },
    props: {
        context: {
            type: Object,
            default: () => ({})
        },
        value: {
            type: String,
            default: ""
        },
        isPossibleHtml: {
            type: Boolean,
            default: false
        },
        autoOpenModal: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        textString() {
            try {
                if (this.valueEval === null && this.value && typeof this.context.Translate === 'function')
                    this.valueEval = utils.compile(this.context, this.context.Translate(this.value));

                if (this.valueEval && typeof utils.evaluate === 'function') {
                    const res = utils.evaluate(this.valueEval, this.context, false);
                    this.$emit("grid-exported-text", res);
                    return res;
                }
                this.$emit("grid-exported-text", '');
                return '';
            }
            catch (e) {
                utils.warn('TranslationContainer textString could not evaluate expression: ' + this.value + '; ' + e);
                return '';
            }
        }
    },
    watch: {
        value() {
            this.valueEval = null;
        }
    },
    methods: {
        async openEdit(e) {
            this.stopPropagation(e);

            if (!this.keyModel) {
                let res;
                try {
                    res = await api.get(`Apps/Translation/TranslationKey?TranslationKeyID=${encodeURIComponent(this.value)}`);
                }
                catch (e) {
                    utils.warn('Failed to get translation key');
                }

                if (res?.data?.Result) {
                    this.keyModel = res.data.Result;

                    if (this.keyModel.locales.length) {
                        let localeIndex = this.keyModel.locales.findIndex(locale => locale.locale === appSettings.LanguageKey);

                        if (localeIndex >= 0) {
                            this.editedString = this.keyModel.locales[localeIndex].translation;
                            this.originalValue = this.keyModel.locales[localeIndex].translation;
                        }
                    }
                    this.keyModel.saved = true;
                }
                else
                    this.keyModel = { saved: false };
            }
            if (this.autoOpenModal)
                this.expanded = true;
            this.editing = true;
        },
        openExpanded(e) {
            this.stopPropagation(e);
            this.expanded = true;
        },
        closeExpanded(e) {
            this.stopPropagation(e);
            this.expanded = false;
            if (this.autoOpenModal)
                this.editing = false;
        },
        async saveTranslation(e) {
            this.stopPropagation(e);
            if (this.editedString === this.originalValue) {
                this.editing = false;
                return;
            }

            if (this.keyModel.saved) {
                let localeIndex = -1;

                if (!this.keyModel.locales || !Array.isArray(this.keyModel.locales) || !this.keyModel.locales.length)
                    this.keyModel.locales = [
                        {
                            locale: appSettings.LanguageKey,
                            translation: this.editedString
                        }
                    ];
                else {
                    localeIndex = this.keyModel.locales.findIndex(locale => locale.locale === appSettings.LanguageKey);

                    if (localeIndex >= 0)
                        this.keyModel.locales[localeIndex].translation = this.editedString;
                    else
                        this.keyModel.locales.push({
                            locale: appSettings.LanguageKey,
                            translation: this.editedString
                        });
                }
            }
            else {
                this.keyModel = {
                    key: this.value,
                    description: "",
                    customerID: token.CustomerID(),
                    locales: [
                        {
                            locale: appSettings.LanguageKey,
                            translation: this.editedString
                        }
                    ]
                };
            }

            try {
                await api.post('Apps/Translation/TranslationKey', this.keyModel);
                if (!careService.globalTranslationList[this.keyModel.customerID || token.CustomerID()])
                    careService.globalTranslationList[this.keyModel.customerID || token.CustomerID()] = {};
                careService.globalTranslationList[this.keyModel.customerID || token.CustomerID()][this.editedString] = this.editedString;
                this.keyModel = null;
                this.originalValue = this.editedString;
                this.valueEval = utils.compile(this.context, this.context.Translate(this.editedString));
                this.editing = false;
            }
            catch (e) {
                utils.warn('Failed to save translation key ' + this.value);
            }
        },
        async modalSave(e) {
            this.expanded = false;
            await this.saveTranslation(e);
        },
        stopPropagation(e) {
            e.stopPropagation();
            return;
        }
    },
    render() {
        if (!this.value)
            return null;

        let theContent = this.textString;

        if (this.isPossibleHtml && theContent.includes('<') && theContent.includes('>'))
            theContent = <div innerHTML={theContent}></div>;

        if (appSettings.DebugTranslationPrefixSetting && typeof this.context.isExpressionOnly === 'function' && !this.context.isExpressionOnly(this.value)) {
            let content;

            if (this.editing)
                content = [
                    <QBtn flat round dense size="sm"
                        onClick={this.saveTranslation}
                        onMousedown={this.stopPropagation}
                        onMouseup={this.stopPropagation}
                        onFocus={this.stopPropagation}
                        style={{ color: 'inherit' }}
                    >
                        <QIcon name="mdi-check" style={{ color: 'inherit' }} />
                    </QBtn>,
                    <div
                        onFocus={this.stopPropagation}
                        onClick={this.stopPropagation}
                        onMousedown={this.stopPropagation}
                        onMouseup={this.stopPropagation}
                    >
                        <QInput
                            dense
                            outlined
                            modelValue={this.editedString}
                            onUpdate:modelValue={(v) => this.editedString = v}
                            onFocus={this.stopPropagation}
                            onClick={this.stopPropagation}
                            onMousedown={this.stopPropagation}
                            onMouseup={this.stopPropagation}
                            class="pt-1 pb-1"
                            dark={false}
                        >
                            {{ append: () => <QBtn flat dense round size="sm" onClick={this.openExpanded}><QIcon name="mdi-arrow-expand" /></QBtn> }}
                        </QInput>
                    </div>,
                    <QDialog modelValue={this.expanded} onUpdate:modelValue={(v) => this.expanded = v} persistent>
                        <QCard>
                            <QCardSection class="row items-center">
                                <QSpace />
                                <QBtn flat round dense onClick={this.closeExpanded}>
                                    <QIcon name="mdi-close" />
                                </QBtn>
                            </QCardSection>
                            <QCardSection>
                                <div class="d-flex flex-column flex-grow-1 mb-2">
                                    <TranslationContainer class="mr-1 text-h6 font-weight-bold" context={this.context} value="Original: "></TranslationContainer>
                                    <span class="mr-auto">{this.value}</span>
                                </div>
                                <QInput type="textarea" modelValue={this.editedString} onUpdate:modelValue={(v) => this.editedString = v} outlined dense />
                            </QCardSection>
                            <QCardActions align="right">
                                <QSpace />
                                <QBtn flat color="primary" onClick={this.modalSave}>
                                    <TranslationContainer context={this.context} value="Save"></TranslationContainer>
                                </QBtn>
                            </QCardActions>
                        </QCard>
                    </QDialog>
                ];
            else
                content = [
                    <QBtn flat round dense size="sm"
                        onClick={this.openEdit}
                        onFocus={this.stopPropagation}
                        onMousedown={this.stopPropagation}
                        onMouseup={this.stopPropagation}
                        style={{ color: 'inherit' }}
                    >
                        <QIcon name="mdi-pencil" style={{ color: 'inherit' }} />
                    </QBtn>,
                    <span>{theContent}</span>
                ];

            return (
                <div class="d-flex align-center translation-container">
                    {content}
                </div>
            );
        }
        else {
            return (
                <span style={{ cursor: 'inherit' }}>{theContent}</span>
            );
        }
    }
});